import React, { useEffect, useState, useContext, useRef, JSX } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDollar, faSave, faCopy, faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import Swal from 'sweetalert2'
import countries from '../../countries.json'
import SelectSearch from 'react-select-search'
import AuthContext from '../../middlewares/AuthContext'
import utilities from '../../utilities/utilities'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface StoreOption {
  name : string
  value: string
}

interface PaymentForm {
  email      : string
  price      : number | string
  name       : string
  description: string
  TimeId     : number
  expiry_time: number
}

const formSchema = Yup.object().shape({
  email      : Yup.string().email().required(),
  price      : Yup.number().min(0).max(999.99).required(),
  name       : Yup.string().trim().required(),
  description: Yup.string().trim().required(),
  TimeId     : Yup.number().integer().min(1).max(3).required(),
  expiry_time: Yup.number().integer().min(1).max(31).required(),
})

const INITIAL: PaymentForm = {
  email      : '',
  price      : '',
  name       : '',
  description: '',
  TimeId     : 0,
  expiry_time: 0,
}

const EXPIRY_MAX: Record<number, number> = { 1: 3, 2: 25, 3: 32 }

const getExpiryOptions = (timeId: number): JSX.Element[] => {
  const max = EXPIRY_MAX[timeId] ?? 0
  return Array.from({ length: max - 1 }, (_, i) => (
    <option key={i + 1} value={i + 1}>{i + 1}</option>
  ))
}

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const CreateStorePayment: React.FC = () => {
  const { getHeaders }      = useContext(AuthContext) as AuthCtx
  const { t }               = useTranslation()
  const { state }           = useLocation() as { state: { store?: string } | null }

  const modalTriggerRef     = useRef<HTMLButtonElement>(null)

  const [loading, setLoading]     = useState(false)
  const [country, setCountry]     = useState('')
  const [countryErr, setCountryErr] = useState(false)
  const [store, setStore]         = useState(state?.store ?? '')
  const [storeErr, setStoreErr]   = useState(false)
  const [stores, setStores]       = useState<StoreOption[]>([])
  const [copyUrl, setCopyUrl]     = useState('')
  const [checkoutId, setCheckoutId] = useState('')
  const [payerEmail, setPayerEmail] = useState('')
  const [alertCopy, setAlertCopy] = useState(false)
  const [alertSend, setAlertSend] = useState(false)

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const headers = await getHeaders()
        const res     = await axios.get<{ message: StoreOption[] }>(`${url}stores/available`, { headers })
        setStores(res.data.message)
      } catch { /* non-critical */ }
    }
    fetchStores()
  }, [])

  const handleSubmit = async (values: PaymentForm, { resetForm }: { resetForm: () => void }) => {
    if (!country.trim()) { setCountryErr(true); return }
    setCountryErr(false)
    if (!store.trim()) { setStoreErr(true); return }
    setStoreErr(false)

    const countryData = (countries as any[]).find((c: any) => c.name === country)
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.post<{ url: string; cs_checkout_id: string }>(
        `${url}stores/payment`,
        { ...values, country, country_iso: countryData?.iso2 ?? '', store },
        { headers }
      )
      setCopyUrl(res.data.url)
      setCheckoutId(res.data.cs_checkout_id)
      setPayerEmail(values.email as string)
      setCountry('')
      setStore('')
      resetForm()
      modalTriggerRef.current?.click()
    } catch (err: any) {
      Swal.fire({
        icon : 'error',
        title: err?.response?.data?.response || 'Error',
        text : err?.response?.data?.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = () => {
    utilities.copyToClipboard(copyUrl)
    setAlertCopy(true)
    setTimeout(() => setAlertCopy(false), 3000)
  }

  const sendEmail = async () => {
    const result = await Swal.fire({
      title            : 'Confirm send email?',
      icon             : 'info',
      showCancelButton : true,
      confirmButtonText: 'Send!',
    })
    if (!result.isConfirmed) return
    setAlertSend(true)
    try {
      const headers = await getHeaders()
      await axios.post(`${url}stores/send-email`, { email: payerEmail, url: copyUrl, cs_checkout_id: checkoutId }, { headers })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setTimeout(() => setAlertSend(false), 3000)
    }
  }

  return (
    <>
      {/* ── Hidden modal trigger ─────────────────────────────────────── */}
      <button
        type="button"
        className="d-none"
        data-bs-toggle="modal"
        data-bs-target="#paymentLinkModal"
        ref={modalTriggerRef}
      />

      {/* ── Payment link modal ───────────────────────────────────────── */}
      <div
        className="modal fade"
        id="paymentLinkModal"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex={-1}
        aria-labelledby="paymentLinkModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold" id="paymentLinkModalLabel">Payment link created</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={alertSend}
              />
            </div>
            <div className="modal-body text-center">
              <p className="text-muted small mb-2">You can copy this link to pay with Stripe</p>
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '0.75rem 1rem', border: '1px solid #e2e8f0', wordBreak: 'break-all' }}>
                <code style={{ fontSize: '0.85rem', color: '#1e293b' }}>{copyUrl}</code>
              </div>
            </div>
            <div className="modal-footer" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.75rem' }}>
              {alertCopy && (
                <div className="alert alert-success py-2 mb-0 animate__animated animate__fadeIn" role="alert">
                  URL copied to clipboard
                </div>
              )}
              {alertSend && (
                <div className="alert alert-success py-2 mb-0 animate__animated animate__fadeIn" role="alert">
                  Email sending…
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={alertSend}
                  onClick={sendEmail}
                  style={{
                    display        : 'inline-flex',
                    alignItems     : 'center',
                    gap            : '0.4rem',
                    padding        : '0.45rem 1.1rem',
                    borderRadius   : 9999,
                    backgroundColor: '#22c55e',
                    color          : '#fff',
                    fontWeight     : 700,
                    fontSize       : '0.82rem',
                    border         : 'none',
                    cursor         : alertSend ? 'not-allowed' : 'pointer',
                    boxShadow      : '0 3px 8px #22c55e44',
                  }}
                >
                  {alertSend
                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    : <FontAwesomeIcon icon={faPaperPlane} />
                  }
                  <span>Send to {payerEmail}</span>
                </button>
                <button
                  type="button"
                  onClick={copyToClipboard}
                  style={{
                    display        : 'inline-flex',
                    alignItems     : 'center',
                    gap            : '0.4rem',
                    padding        : '0.45rem 1.1rem',
                    borderRadius   : 9999,
                    backgroundColor: '#3b82f6',
                    color          : '#fff',
                    fontWeight     : 700,
                    fontSize       : '0.82rem',
                    border         : 'none',
                    cursor         : 'pointer',
                    boxShadow      : '0 3px 8px #3b82f644',
                  }}
                >
                  <FontAwesomeIcon icon={faCopy} />
                  <span>Copy URL</span>
                </button>
              </div>
              <p className="text-muted small mb-0">
                <small>* Include CC to contact@labeldictate.com</small>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Page ─────────────────────────────────────────────────────── */}
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">

          {/* Header card */}
          <div style={{
            backgroundColor : '#fff',
            borderRadius    : 16,
            boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
            border          : '1px solid #f1f5f9',
            overflow        : 'hidden',
            marginBottom    : '1.5rem',
          }}>
            <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
            <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{
                width           : 40,
                height          : 40,
                borderRadius    : 10,
                backgroundColor : `${BRAND}18`,
                display         : 'flex',
                alignItems      : 'center',
                justifyContent  : 'center',
                flexShrink      : 0,
              }}>
                <FontAwesomeIcon icon={faDollar} style={{ color: BRAND, fontSize: '1.1rem' }} />
              </div>
              <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
                {t('stores.payment.create')}
              </p>
            </div>
          </div>

          {/* Form card */}
          <div style={{
            backgroundColor : '#fff',
            borderRadius    : 16,
            boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
            border          : '1px solid #f1f5f9',
            overflow        : 'hidden',
          }}>
            <div style={{ height: 3, backgroundColor: BRAND }} />
            <div style={{ padding: '1.5rem' }}>
              <Formik
                initialValues={INITIAL}
                validationSchema={formSchema}
                onSubmit={handleSubmit}
              >
                {({ values, setFieldValue }) => (
                  <Form>

                    <div className="mb-4">
                      <FieldLabel>{t('login.email')}</FieldLabel>
                      <Field type="email" name="email" className="form-control form-control-sm" placeholder="name@example.com" />
                      <ErrorMessage name="email" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    <div className="mb-4">
                      <FieldLabel>{t('stores.payment.price')}</FieldLabel>
                      <Field type="number" name="price" className="form-control form-control-sm" step="0.01" min="0" max="999.99" />
                      <ErrorMessage name="price" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    <div className="mb-4">
                      <FieldLabel>{t('stores.name')}</FieldLabel>
                      <Field type="text" name="name" className="form-control form-control-sm" />
                      <ErrorMessage name="name" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    <div className="mb-4">
                      <FieldLabel>{t('stores.description')}</FieldLabel>
                      <Field type="text" name="description" className="form-control form-control-sm" />
                      <ErrorMessage name="description" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    {/* Country */}
                    <div className="mb-4">
                      <FieldLabel>{t('stores.country')}</FieldLabel>
                      <SelectSearch
                        options={countries as any}
                        value={country}
                        onChange={(v: any) => { setCountry(v); setCountryErr(false) }}
                        // name="country"
                        placeholder={t('stores.payment.chose_country')}
                        search
                        autoComplete="off"
                      />
                      {countryErr && (
                        <p className="text-danger small fw-bold mt-1 mb-0">Select a country</p>
                      )}
                    </div>

                    {/* Store */}
                    {stores.length > 0 && (
                      <div className="mb-4">
                        <FieldLabel>{t('stores.payment.chose_store')}</FieldLabel>
                        <SelectSearch
                          options={stores}
                          value={store}
                          onChange={(v: any) => { setStore(v); setStoreErr(false) }}
                          // name="store"
                          placeholder={t('stores.payment.chose_store')}
                          search
                        />
                        {storeErr && (
                          <p className="text-danger small fw-bold mt-1 mb-0">{t('stores.payment.chose_store')}</p>
                        )}
                      </div>
                    )}

                    {/* Time unit */}
                    <div className="mb-4">
                      <FieldLabel>{t('stores.payment.select_an_time')}</FieldLabel>
                      <Field
                        as="select"
                        name="TimeId"
                        className="form-select form-select-sm"
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          setFieldValue('TimeId', e.target.value)
                          setFieldValue('expiry_time', 0)
                        }}
                      >
                        <option value={0} disabled>{t('stores.payment.select_an_time')}</option>
                        {(['years', 'months', 'days'] as const).map((unit, i) => (
                          <option key={unit} value={i + 1}>{t(`utilities.${unit}`)}</option>
                        ))}
                      </Field>
                      <ErrorMessage name="TimeId" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    {/* Expiry quantity */}
                    <div className="mb-4">
                      <FieldLabel>{t('stores.payment.select_expire_time')}</FieldLabel>
                      <Field as="select" name="expiry_time" className="form-select form-select-sm">
                        <option value={0} disabled>{t('stores.payment.select_expire_time')}</option>
                        {getExpiryOptions(Number(values.TimeId))}
                      </Field>
                      <ErrorMessage name="expiry_time" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        display        : 'inline-flex',
                        alignItems     : 'center',
                        justifyContent : 'center',
                        gap            : '0.5rem',
                        width          : '100%',
                        padding        : '0.55rem 1.5rem',
                        borderRadius   : 9999,
                        backgroundColor: loading ? '#94a3b8' : BRAND,
                        color          : '#fff',
                        fontWeight     : 700,
                        fontSize       : '0.875rem',
                        border         : 'none',
                        cursor         : loading ? 'not-allowed' : 'pointer',
                        boxShadow      : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                        transition     : 'filter 0.15s, transform 0.15s',
                      }}
                      onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                      onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                    >
                      {loading
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        : <FontAwesomeIcon icon={faSave} />
                      }
                      <span>{t('stores.payment.create_pay')}</span>
                    </button>

                  </Form>
                )}
              </Formik>
            </div>
          </div>

        </div>
      </div>
    </>
  )
}

export default CreateStorePayment
